import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Activity, LayoutGrid, Trash2, Users } from "lucide-react";
import AlertNotification from "../../components/notification";
import DeleteModal from "../../components/models/deleteModal.jsx";
import ActivityLogTab from "../../components/project-management/ActivityLogTab";
import DataTable, { createActionsColumn } from "../../components/data-table";
import api from "../../services/apiService";
import { usePageTitle } from "../../components/layouts/PageTitleContext";

const recruitStatusByProjectType = {
  Surveys: ["Link sent", "DQed", "Completed", "Declined by client"],
  Interviews: ["Awaiting Availability", "Scheduled", "Completed", "Rescheduling", "Canceled"],
};

const defaultProject = {
  projectId: "PRJ-1002",
  projectName: "Client Portal V2",
  status: "In Progress",
  priority: "Moderate",
  totalTasks: 20,
  completedTasks: 14,
  percentage: 70,
  targets: "Phase 1 UAT",
  startDate: "2026-02-05",
  dueDate: "2026-08-01",
  assignedUsers: ["Mina Ashraf", "Farhan Iqbal", "Leo Turner"],
  notes:
    "Please complete all interviews before Friday.\n\nSurvey Link:\nhttps://forms.google.com/example\n\nResources:\nhttps://companywiki.com/project-docs\n\nTeam Instructions:\n- Complete candidate screening.\n- Submit feedback within 24 hours.\n- Update project status every week.",
};

const initialRecruits = [
  {
    id: "RC-001",
    backendId: null,
    name: "Ayesha Noor",
    status: "Interview",
    owner: "Mina Ashraf",
    createdDate: "2026-06-01",
  },
  {
    id: "RC-002",
    backendId: null,
    name: "Usman Tariq",
    status: "Screening",
    owner: "Farhan Iqbal",
    createdDate: "2026-06-02",
  },
  {
    id: "RC-003",
    backendId: null,
    name: "Nadia Ali",
    status: "Shortlisted",
    owner: "Leo Turner",
    createdDate: "2026-06-03",
  },
];

const formatDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const isUrl = (value) => /^https?:\/\/\S+$/i.test(value);

const renderLineWithLinks = (line, lineIndex, totalLines) => {
  const parts = line.split(/(https?:\/\/\S+)/g);

  return (
    <React.Fragment key={`line-${lineIndex}`}>
      {parts.map((part, partIndex) => {
        if (isUrl(part)) {
          return (
            <a
              key={`part-${lineIndex}-${partIndex}`}
              href={part}
              target="_blank"
              rel="noreferrer"
              className="text-[#2470B7] underline break-all hover:text-[#1f5f9b] dark:text-blue-400 dark:hover:text-blue-300"
            >
              {part}
            </a>
          );
        }

        return <span key={`part-${lineIndex}-${partIndex}`}>{part}</span>;
      })}
      {lineIndex < totalLines - 1 && <br />}
    </React.Fragment>
  );
};

function FieldRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-app-primary py-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-app-muted">{label}</span>
      <span className="text-sm font-medium text-app-secondary text-right">{value}</span>
    </div>
  );
}

function ProjectBoard() {
  const { state } = useLocation();
  const { projectId: routeProjectId } = useParams();
  const project = state?.project || defaultProject;
  usePageTitle(project.projectName);
  const backendProjectId = Number(project?.backendId ?? project?.id ?? routeProjectId);

  const recruitStatusOptions = useMemo(() => {
    const projectType = String(project?.projectType || "").trim().toLowerCase();

    if (projectType.includes("survey")) {
      return recruitStatusByProjectType.Surveys;
    }

    if (projectType.includes("interview")) {
      return recruitStatusByProjectType.Interviews;
    }

    return [];
  }, [project.projectType]);

  const getSafeStatus = (status) => {
    if (recruitStatusOptions.includes(status)) return status;
    return recruitStatusOptions[0] || "";
  };

  const [recruits, setRecruits] = useState(() =>
    initialRecruits.map((recruit) => ({
      ...recruit,
      status: getSafeStatus(recruit.status),
    }))
  );
  const [newRecruitName, setNewRecruitName] = useState("");
  const [newRecruitStatus, setNewRecruitStatus] = useState(recruitStatusOptions[0] || "");
  const [notification, setNotification] = useState(null);
  const [isSubmittingRecruit, setIsSubmittingRecruit] = useState(false);
  const [isLoadingRecruits, setIsLoadingRecruits] = useState(false);
  const [updatingRecruitId, setUpdatingRecruitId] = useState("");
  const [showDeleteRecruitModal, setShowDeleteRecruitModal] = useState(false);
  const [recruitToDelete, setRecruitToDelete] = useState(null);
  const [isDeletingRecruit, setIsDeletingRecruit] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const normalizedDefaultStatus = recruitStatusOptions[0] || "";

    setRecruits((prev) =>
      prev.map((recruit) => ({
        ...recruit,
        status: recruitStatusOptions.includes(recruit.status)
          ? recruit.status
          : normalizedDefaultStatus,
      }))
    );

    if (!recruitStatusOptions.includes(newRecruitStatus)) {
      setNewRecruitStatus(normalizedDefaultStatus);
    }
  }, [recruitStatusOptions, newRecruitStatus]);

  useEffect(() => {
    let isActive = true;

    const fetchRecruits = async () => {
      if (!Number.isFinite(backendProjectId)) return;

      setIsLoadingRecruits(true);
      try {
        const token = localStorage.getItem("token");
        const response = await api.get(`/projects/${backendProjectId}/recruits`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        if (!isActive) return;

        const mapped = list.map((recruit, index) => {
          const rawId = recruit?.id;
          const displayId = rawId
            ? `RC-${String(rawId).padStart(3, "0")}`
            : `RC-TMP-${String(index + 1).padStart(3, "0")}`;

          return {
            id: displayId,
            backendId: Number.isFinite(Number(rawId)) ? Number(rawId) : null,
            name: recruit?.recruitName || recruit?.name || "Unnamed Recruit",
            status: recruit?.status || "",
            owner:
              recruit?.ownerEmail ||
              recruit?.owner?.email ||
              recruit?.createdByUser?.email ||
              project.assignedUsers?.[0] ||
              "Unassigned",
            createdDate:
              recruit?.createdAt || recruit?.createdDate || new Date().toISOString().split("T")[0],
          };
        });

        setRecruits(mapped);
      } catch (error) {
        console.error("Failed to fetch recruits", error);
        if (isActive) {
          setNotification({ text: "Failed to fetch recruits.", status: false });
        }
      } finally {
        if (isActive) {
          setIsLoadingRecruits(false);
        }
      }
    };

    fetchRecruits();

    return () => {
      isActive = false;
    };
  }, [backendProjectId, project.assignedUsers]);

  const completedRecruitCount = useMemo(
    () =>
      recruits.filter(
        (recruit) => String(recruit?.status || "").trim().toLowerCase() === "completed"
      ).length,
    [recruits]
  );

  const completionPercent = useMemo(() => {
    const total = Number(project.totalTasks) || 0;
    if (!total) return 0;
    return Math.min(100, Math.round((completedRecruitCount / total) * 100));
  }, [completedRecruitCount, project.totalTasks]);

  const handleAddRecruit = async (event) => {
    event.preventDefault();
    const trimmedName = newRecruitName.trim();
    if (!trimmedName) return;
    if (!recruitStatusOptions.includes(newRecruitStatus)) return;

    setIsSubmittingRecruit(true);

    let newRecruit = null;

    if (Number.isFinite(backendProjectId)) {
      try {
        const token = localStorage.getItem("token");
        const response = await api.post(
          `/projects/${backendProjectId}/recruits`,
          {
            recruitName: trimmedName,
            status: newRecruitStatus,
          },
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              "Content-Type": "application/json",
            },
          }
        );

        const createdRecruit = response?.data?.data || response?.data || {};
        const recruitId = createdRecruit?.id;

        newRecruit = {
          id: recruitId ? `RC-${String(recruitId).padStart(3, "0")}` : `RC-${Date.now()}`,
          backendId: Number.isFinite(Number(recruitId)) ? Number(recruitId) : null,
          name: createdRecruit?.recruitName || trimmedName,
          status: createdRecruit?.status || newRecruitStatus,
          owner: createdRecruit?.ownerEmail || project.assignedUsers?.[0] || "Unassigned",
          createdDate:
            (createdRecruit?.createdAt && createdRecruit.createdAt.split("T")[0]) ||
            new Date().toISOString().split("T")[0],
        };
      } catch (error) {
        console.error("Failed to add recruit", error);
        setNotification({ text: "Failed to add recruit.", status: false });
        setIsSubmittingRecruit(false);
        return;
      }
    } else {
      const numericIds = recruits
        .map((recruit) => parseInt(recruit.id.replace(/\D/g, ""), 10))
        .filter((value) => Number.isFinite(value));
      const nextIdNumber = (numericIds.length ? Math.max(...numericIds) : 0) + 1;

      newRecruit = {
        id: `RC-${String(nextIdNumber).padStart(3, "0")}`,
        backendId: null,
        name: trimmedName,
        status: newRecruitStatus,
        owner: project.assignedUsers?.[0] || "Unassigned",
        createdDate: new Date().toISOString().split("T")[0],
      };
    }

    setRecruits((prev) => [newRecruit, ...prev]);
    setNewRecruitName("");
    setNewRecruitStatus(recruitStatusOptions[0] || "");
    setIsSubmittingRecruit(false);
    setNotification({ text: "Recruit added successfully!", status: true });
  };

  const handleStatusUpdate = async (recruitId, nextStatus) => {
    if (!recruitStatusOptions.includes(nextStatus)) return;

    const selectedRecruit = recruits.find((recruit) => recruit.id === recruitId);
    if (!selectedRecruit) return;

    const previousStatus = selectedRecruit.status;
    if (previousStatus === nextStatus) return;

    setRecruits((prev) =>
      prev.map((recruit) =>
        recruit.id === recruitId ? { ...recruit, status: nextStatus } : recruit
      )
    );

    if (!Number.isFinite(Number(selectedRecruit.backendId))) {
      return;
    }

    setUpdatingRecruitId(recruitId);
    try {
      const token = localStorage.getItem("token");
      await api.put(
        `/recruits/${selectedRecruit.backendId}`,
        { status: nextStatus },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        }
      );
      setNotification({ text: "Recruit status updated.", status: true });
    } catch (error) {
      console.error("Failed to update recruit status", error);
      setRecruits((prev) =>
        prev.map((recruit) =>
          recruit.id === recruitId ? { ...recruit, status: previousStatus } : recruit
        )
      );
      setNotification({ text: "Failed to update recruit status.", status: false });
    } finally {
      setUpdatingRecruitId("");
    }
  };

  const handleDeleteRecruit = (recruit) => {
    setRecruitToDelete(recruit);
    setShowDeleteRecruitModal(true);
  };

  const handleCloseDeleteRecruitModal = () => {
    if (isDeletingRecruit) return;
    setShowDeleteRecruitModal(false);
    setRecruitToDelete(null);
  };

  const handleConfirmDeleteRecruit = async () => {
    if (!recruitToDelete) return;

    const recruitId = recruitToDelete.id;
    const backendRecruitId = Number(recruitToDelete.backendId);

    if (!Number.isFinite(backendRecruitId)) {
      setRecruits((prev) => prev.filter((recruit) => recruit.id !== recruitId));
      setNotification({ text: "Recruit deleted successfully!", status: true });
      setShowDeleteRecruitModal(false);
      setRecruitToDelete(null);
      return;
    }

    setIsDeletingRecruit(true);
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/recruits/${backendRecruitId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      setRecruits((prev) => prev.filter((recruit) => recruit.id !== recruitId));
      setNotification({ text: "Recruit deleted successfully!", status: true });
      setShowDeleteRecruitModal(false);
      setRecruitToDelete(null);
    } catch (error) {
      console.error("Failed to delete recruit", error);
      setNotification({ text: "Failed to delete recruit.", status: false });
    } finally {
      setIsDeletingRecruit(false);
    }
  };

  const recruitColumns = [
    {
      key: "id",
      label: "Recruit ID",
      sortable: true,
      render: (v) => <span className="font-semibold text-app-secondary">{v}</span>,
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (v) => <span className="font-medium text-app-primary">{v}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (v, recruit) => (
        <select
          value={v}
          onChange={(event) => handleStatusUpdate(recruit.id, event.target.value)}
          disabled={!recruitStatusOptions.length || updatingRecruitId === recruit.id}
          aria-label={`Status for ${recruit.name}`}
          className="h-8 rounded-lg border border-app-primary bg-app-input py-0 pl-2.5 pr-8 text-xs font-medium text-app-primary focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
        >
          {recruitStatusOptions.map((status) => (
            <option key={`${recruit.id}-${status}`} value={status}>{status}</option>
          ))}
        </select>
      ),
    },
    { key: "owner", label: "Owner", sortable: true, render: (v) => v || "-" },
    {
      key: "createdDate",
      label: "Created Date",
      sortable: true,
      searchable: false,
      render: (v) => formatDate(v),
    },
    createActionsColumn(
      (recruit) => [
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          disabled: isDeletingRecruit && recruitToDelete?.id === recruit.id,
          onSelect: () => handleDeleteRecruit(recruit),
        },
      ],
      { getRowLabel: (recruit) => recruit.name }
    ),
  ];

  return (
    <div className="p-5 w-full max-w-full">
      {(project.priority || project.dueDate) && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          {project.priority && (
            <span className="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-3 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300">
              {project.priority}
            </span>
          )}
          {project.dueDate && (
            <span className="inline-flex items-center gap-1 text-sm text-app-muted">
              <span className="font-medium text-app-muted">Due</span>
              {formatDate(project.dueDate)}
            </span>
          )}
        </div>
      )}

      <div className="flex items-start gap-5">
        {/* Center: Tab bar + content */}
        <main className="min-w-0 flex-1 space-y-5">
          {/* Horizontal tab strip */}
          <div className="flex gap-2 border-b border-app-primary">
            {[
              { key: "overview", label: "Overview", icon: LayoutGrid },
              { key: "recruits", label: "Recruit Records", icon: Users },
              { key: "activity", label: "Activity Log", icon: Activity },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`-mb-px inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
                  activeTab === key
                    ? "border-[#2470B7] text-[#2470B7] dark:border-blue-400 dark:text-blue-400"
                    : "border-transparent text-slate-500 hover:bg-app-hover hover:text-[#2470B7] dark:text-slate-400 dark:hover:text-blue-300"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {/* Project Information */}
              <section className="rounded-xl border border-app-primary bg-app-card p-5 shadow-sm dark:shadow-none">
                <h3 className="text-lg font-semibold text-app-heading">Project Information</h3>
                <p className="mt-1 text-sm text-app-muted">Key details and delivery health overview.</p>
                <div className="mt-4 space-y-1">
                  <FieldRow label="Project" value={project.projectName || "-"} />
                  <FieldRow label="Status" value={project.status || "-"} />
                  <FieldRow label="Priority" value={project.priority || "-"} />
                  <FieldRow label="Total Targets" value={`${completedRecruitCount}/${project.totalTasks || 0}`} />
                  <FieldRow label="Assignees" value={(project.assignedUsers || []).join(", ") || "-"} />
                  <FieldRow label="Start Date" value={formatDate(project.startDate)} />
                  <FieldRow label="Due Date" value={formatDate(project.dueDate)} />
                </div>
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-app-muted">Recruitment Completion</span>
                    <span className="text-sm font-semibold text-app-secondary">{completionPercent}%</span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-[#2470B7] transition-all dark:bg-blue-500 duration-500"
                      style={{ width: `${completionPercent}%` }}
                    />
                  </div>
                </div>
              </section>

              {/* Notes */}
              <section className="rounded-xl border border-app-primary bg-app-card p-5 shadow-sm dark:shadow-none">
                <h3 className="text-lg font-semibold text-app-heading">Notes</h3>
                <p className="mt-1 text-sm text-app-muted">Internal comments, links, and references.</p>
                <div className="mt-4 max-h-[360px] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-6 text-app-secondary">
                  {(project.notes || "No notes available.")
                    .split("\n")
                    .map((line, index, allLines) => renderLineWithLinks(line, index, allLines.length))}
                </div>
              </section>
            </div>
          )}

          {activeTab === "recruits" && (
            <>
              <section className="rounded-xl border border-app-primary bg-app-card p-5 shadow-sm dark:shadow-none">
                <h3 className="text-lg font-semibold text-app-heading">Add Recruit</h3>
                <p className="mt-1 text-sm text-app-muted">Quickly add candidates and track progress.</p>

                <form onSubmit={handleAddRecruit} className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-12">
                  <div className="lg:col-span-6">
                    <label htmlFor="recruit-name" className="mb-1 block text-sm font-medium text-app-secondary">
                      Recruit Name
                    </label>
                    <input
                      id="recruit-name"
                      type="text"
                      value={newRecruitName}
                      onChange={(event) => setNewRecruitName(event.target.value)}
                      className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                      placeholder="Enter recruit name"
                    />
                  </div>

                  <div className="lg:col-span-4">
                    <label htmlFor="recruit-status" className="mb-1 block text-sm font-medium text-app-secondary">
                      Status
                    </label>
                    <select
                      id="recruit-status"
                      value={newRecruitStatus}
                      onChange={(event) => setNewRecruitStatus(event.target.value)}
                      disabled={!recruitStatusOptions.length}
                      className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    >
                      {recruitStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="lg:col-span-2 flex items-end">
                    <button
                      type="submit"
                      disabled={!recruitStatusOptions.length || isSubmittingRecruit}
                      className="w-full rounded-lg border border-[#2470B7] bg-[#2470B7] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
                    >
                      {isSubmittingRecruit ? "Adding..." : "Add Recruit"}
                    </button>
                  </div>
                </form>
              </section>

              <section className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-app-heading">Recruits</h3>
                  <p className="mt-1 text-sm text-app-muted">Manage candidates and update statuses inline.</p>
                </div>

                <DataTable
                  ariaLabel="Recruits"
                  columns={recruitColumns}
                  data={recruits}
                  loading={isLoadingRecruits}
                  searchable
                  searchPlaceholder="Search recruit ID, name or owner"
                  pagination
                  defaultPageSize={10}
                  maxHeight="560px"
                  labels={{ empty: "No recruits found." }}
                />
              </section>
            </>
          )}

          {activeTab === "activity" && <ActivityLogTab projectId={backendProjectId} />}
        </main>

      </div>
      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
      <DeleteModal
        show={showDeleteRecruitModal}
        handleClose={handleCloseDeleteRecruitModal}
        handleConfirm={handleConfirmDeleteRecruit}
        message="Are you sure you want to delete this?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeletingRecruit}
      />
    </div>
  );
}

export default ProjectBoard;
